<template>
	<div ref="outerEl" class="min-w-0 w-full overflow-hidden relative">
		<Transition name="fade">
			<div
				v-if="showCover && isAbleToScroll && offsetX"
				class="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-white pointer-events-none z-10"
			/>
		</Transition>
		<div ref="innerEl" class="inline-flex min-w-full">
			<slot v-bind="{scrollToView}" />
		</div>
	</div>
</template>

<script setup lang="ts">
	import {gsap} from 'gsap';
	import {Draggable} from 'gsap/Draggable';
	import {InertiaPlugin} from 'gsap/InertiaPlugin';

	if (import.meta.client) {
		gsap.registerPlugin(Draggable, InertiaPlugin);
	}

	type Props = {
		activeSlug: string;
		showCover?: boolean;
	};

	const props = defineProps<Props>();

	const outerEl = ref<HTMLElement | null>(null);
	const innerEl = ref<HTMLElement | null>(null);
	const draggable = ref<Draggable | null>(null);

	const {width: outerWidth} = useElementSize(outerEl);
	const {width: innerWidth} = useElementSize(innerEl);
	const offsetX = ref(1);

	const isAbleToScroll = computed(() => innerWidth.value > outerWidth.value);

	watchDebounced([isAbleToScroll, innerEl, outerEl], () => {
		if (!innerEl.value || !outerEl.value) {
			return;
		}

		if (isAbleToScroll.value) {
			draggable.value = new Draggable(innerEl.value, {
				type: 'x',
				bounds: outerEl.value,
				edgeResistance: 0.9,
				inertia: true,
				onDragEnd(): void {
					offsetX.value = Math.round(-this.endX);
				},
			});
		} else {
			gsap.set(innerEl.value, {clearProps: true});
			draggable.value?.kill();
			draggable.value = null;
		}
	}, {debounce: 250});

	watch([outerWidth, innerWidth], () => {
		if (draggable.value) {
			draggable.value.update(true);
		}
	});

	watch(() => [draggable.value, props.activeSlug] as const, ([newDraggable, newActiveSLug]) => {
		if (newDraggable?.minX && newActiveSLug) {
			const el = document.querySelector(`*[data-slug="${newActiveSLug}"]`) as HTMLElement;
			if (el) {
				scrollToView(el);
			}
		}
	}, {immediate: true});

	function scrollToView(el: HTMLElement) {
		const minX = draggable.value?.minX;
		if (minX) {
			gsap.to(innerEl.value, {
				duration: 0.5,
				x: Math.max(minX, -el.offsetLeft),
				ease: 'power2.out',
				onComplete() {
					draggable.value?.update(true);
				},
			});
		}
	}
</script>
